<script setup>
import { computed, h, inject, ref, watch } from 'vue';
import { useRoute } from 'vue-router';
import { useI18n } from 'vue-i18n';
import { format } from 'date-fns';
import fromUnixTime from 'date-fns/fromUnixTime';
import { useStore, useMapGetter } from 'dashboard/composables/store';
import { formatTimeTranslated } from 'dashboard/composables/useReportMetrics';
import {
  useVueTable,
  createColumnHelper,
  getCoreRowModel,
} from '@tanstack/vue-table';
import BackButton from 'dashboard/components/widgets/BackButton.vue';
import CsatEmptyState from './components/CsatEmptyState.vue';
import ReportSummaryMetrics from './components/ReportSummaryMetrics.vue';
import Table from 'dashboard/components/table/Table.vue';
import Thumbnail from 'dashboard/components/widgets/Thumbnail.vue';
import Spinner from 'dashboard/components-next/spinner/Spinner.vue';

const { t } = useI18n();
const route = useRoute();
const store = useStore();
const columnHelper = createColumnHelper();
const reportFilters = inject('contactReportFilters', ref({ from: 0, to: 0 }));

const hasFetched = ref(false);
const report = useMapGetter('summaryReports/getCompanySummaryReport');
const uiFlags = useMapGetter('summaryReports/getUIFlags');
const isLoading = computed(
  () => !hasFetched.value || uiFlags.value.isFetchingCompanySummaryReport
);

const company = computed(() => report.value?.company || null);
const metrics = computed(() => report.value?.metrics || {});
const operation = computed(() => report.value?.operation || {});
const labels = computed(() => report.value?.labels || []);
const contacts = computed(() => report.value?.contacts || []);

const companyName = computed(() => {
  if (!company.value) return '';
  if (company.value.unassigned || company.value.id == null) {
    return t('CONTACT_REPORTS.UNASSIGNED');
  }
  return company.value.name || company.value.domain || `#${company.value.id}`;
});

const renderCount = value =>
  value == null ? '--' : Number(value).toLocaleString('pt-BR');

const renderAvgTime = value => (value ? formatTimeTranslated(value) : '--');

const formatShare = (part, total) => {
  if (!total) return '--';
  const percent = Math.round((Number(part) / Number(total)) * 1000) / 10;
  return `${percent.toLocaleString('pt-BR')}%`;
};

const periodLabel = computed(() => {
  const current = reportFilters.value;
  if (!current?.from || !current?.to) return '';
  return t('CONTACT_REPORTS.PERIOD', {
    from: format(fromUnixTime(current.from), 'dd/MM/yyyy'),
    to: format(fromUnixTime(current.to), 'dd/MM/yyyy'),
  });
});

const summaryLabel = computed(() =>
  t('CONTACT_REPORTS.DETAIL.SUMMARY', {
    engagements: renderCount(metrics.value.conversationsCount),
    conversations: renderCount(metrics.value.conversationsCount),
    contacts: renderCount(metrics.value.contactsCount),
  })
);

const metricItems = source => [
  {
    label: t('CONTACT_REPORTS.METRICS.ENGAGEMENTS'),
    value: renderCount(source.conversationsCount),
    info: t('CONTACT_REPORTS.METRICS.ENGAGEMENTS_INFO'),
  },
  {
    label: t('CONTACT_REPORTS.METRICS.CONTACTS'),
    value: renderCount(source.contactsCount),
    info: t('CONTACT_REPORTS.METRICS.CONTACTS_INFO'),
  },
  {
    label: t('CONTACT_REPORTS.METRICS.MESSAGES'),
    value: renderCount(source.messagesCount),
    info: t('CONTACT_REPORTS.METRICS.MESSAGES_INFO'),
  },
  {
    label: t('CONTACT_REPORTS.METRICS.RESOLVED'),
    value: renderCount(source.resolvedConversationsCount),
    info: t('CONTACT_REPORTS.METRICS.RESOLVED_INFO'),
  },
  {
    label: t('CONTACT_REPORTS.METRICS.AVG_HANDLING'),
    value: renderAvgTime(source.avgResolutionTime),
    info: t('CONTACT_REPORTS.METRICS.AVG_HANDLING_INFO'),
  },
  {
    label: t('CONTACT_REPORTS.METRICS.FIRST_RESPONSE'),
    value: renderAvgTime(source.avgFirstResponseTime),
    info: t('CONTACT_REPORTS.METRICS.FIRST_RESPONSE_INFO'),
  },
  {
    label: t('CONTACT_REPORTS.METRICS.REPLY_TIME'),
    value: renderAvgTime(source.avgReplyTime),
    info: t('CONTACT_REPORTS.METRICS.REPLY_TIME_INFO'),
  },
];

const companyItems = computed(() => [
  ...metricItems(metrics.value),
  {
    label: t('CONTACT_REPORTS.METRICS.SHARE'),
    value: formatShare(
      metrics.value.conversationsCount,
      operation.value.conversationsCount
    ),
    info: t('CONTACT_REPORTS.METRICS.SHARE_INFO'),
  },
]);

const operationItems = computed(() => [
  {
    label: t('CONTACT_REPORTS.METRICS.COMPANIES'),
    value: renderCount(operation.value.companiesCount),
    info: t('CONTACT_REPORTS.METRICS.COMPANIES_INFO'),
  },
  ...metricItems(operation.value),
]);

const contactDisplayName = contact =>
  contact.name || contact.email || contact.phoneNumber || `#${contact.id}`;

const conversationAnchors = contact =>
  (contact.conversations || []).map(conversation =>
    h(
      'a',
      {
        href: `/app/accounts/${route.params.accountId}/conversations/${conversation.displayId}`,
        class:
          'text-xs text-n-slate-12 hover:text-n-brand hover:underline whitespace-nowrap',
        target: '_blank',
        rel: 'noopener noreferrer nofollow',
      },
      `#${conversation.displayId}`
    )
  );

const contactCell = cellProps => {
  const contact = cellProps.row.original;
  return h('div', { class: 'flex items-center gap-2 min-w-0' }, [
    h(Thumbnail, {
      username: contactDisplayName(contact),
      size: '24px',
    }),
    h('span', { class: 'flex flex-col min-w-0 gap-0.5' }, [
      h(
        'router-link',
        {
          to: {
            name: 'contact_profile_dashboard',
            params: { contactId: contact.id },
          },
          class: 'truncate font-medium text-n-slate-12 hover:underline',
        },
        contactDisplayName(contact)
      ),
      contact.email
        ? h(
            'span',
            { class: 'truncate text-xs text-n-slate-11' },
            contact.email
          )
        : null,
      h(
        'span',
        { class: 'flex flex-wrap items-center gap-x-2 gap-y-0.5' },
        conversationAnchors(contact)
      ),
    ]),
  ]);
};

const columns = computed(() => [
  columnHelper.accessor(row => contactDisplayName(row), {
    id: 'name',
    header: t('CONTACT_REPORTS.TABLE.CONTACT'),
    size: 280,
    cell: contactCell,
  }),
  columnHelper.accessor('conversationsCount', {
    header: t('CONTACT_REPORTS.TABLE.CONVERSATIONS'),
    size: 140,
    cell: cellProps => renderCount(cellProps.getValue()),
  }),
  columnHelper.accessor('messagesCount', {
    header: t('CONTACT_REPORTS.TABLE.MESSAGES'),
    size: 140,
    cell: cellProps => renderCount(cellProps.getValue()),
  }),
]);

const table = useVueTable({
  get data() {
    return contacts.value;
  },
  get columns() {
    return columns.value;
  },
  getCoreRowModel: getCoreRowModel(),
});

const fetchReport = async () => {
  const current = reportFilters.value;
  if (!current?.from || !current?.to || !route.params.companyId) return;

  hasFetched.value = false;
  await store.dispatch('summaryReports/fetchCompanySummaryReport', {
    companyId: route.params.companyId,
    since: current.from,
    until: current.to,
    businessHours: current.businessHours,
    inboxId: current.inboxId,
    teamId: current.teamId,
    agentIds: current.agentIds,
  });
  hasFetched.value = true;
};

watch([() => route.params.companyId, reportFilters], fetchReport, {
  deep: true,
  immediate: true,
});
</script>

<template>
  <section class="flex flex-col gap-1 pt-8 pb-2">
    <BackButton
      compact
      :back-url="{ name: 'contact_reports_index' }"
      :button-label="$t('CONTACT_REPORTS.BACK')"
    />
    <div class="flex flex-col gap-2">
      <span class="text-xl font-medium text-n-slate-12">
        {{ companyName || $t('CONTACT_REPORTS.DETAIL.LOADING_TITLE') }}
      </span>
      <p v-if="company?.domain" class="mb-0 text-sm text-n-slate-11">
        {{ company.domain }}
      </p>
      <p class="mb-0 text-sm text-n-slate-11">
        {{ $t('CONTACT_REPORTS.DETAIL.DESCRIPTION') }}
      </p>
    </div>
  </section>

  <div
    v-if="isLoading"
    class="flex items-center justify-center gap-2 py-16 text-n-slate-11"
  >
    <Spinner />
    <span>{{ $t('CONTACT_REPORTS.DETAIL.LOADING') }}</span>
  </div>

  <CsatEmptyState
    v-else-if="!company"
    :title="$t('CONTACT_REPORTS.NOT_FOUND')"
    :description="$t('CONTACT_REPORTS.NOT_FOUND_DESCRIPTION')"
    icon="i-lucide-building-2"
  />

  <div v-else class="flex flex-col gap-5">
    <div class="rounded-xl border border-n-weak bg-n-solid-2 p-6">
      <div class="mb-4 flex flex-col gap-1">
        <p class="mb-0 text-sm font-medium text-n-slate-12">
          {{ periodLabel }}
        </p>
        <p class="mb-0 text-sm text-n-slate-11">
          {{ summaryLabel }}
        </p>
        <router-link
          v-if="company.id"
          :to="{
            name: 'companies_dashboard_show',
            params: { companyId: company.id },
          }"
          class="text-sm text-n-blue-text hover:underline"
        >
          {{ $t('CONTACT_REPORTS.DETAIL.OPEN_COMPANY') }}
        </router-link>
      </div>
      <ReportSummaryMetrics :items="companyItems" />
    </div>

    <div class="rounded-xl border border-n-weak bg-n-solid-2 p-6">
      <div class="mb-4 flex flex-col gap-1">
        <h3 class="mb-0 text-base font-medium text-n-slate-12">
          {{ $t('CONTACT_REPORTS.DETAIL.LABELS_TITLE') }}
        </h3>
        <p class="mb-0 text-xs text-n-slate-11">
          {{ $t('CONTACT_REPORTS.DETAIL.LABELS_DESCRIPTION') }}
        </p>
      </div>
      <div v-if="labels.length" class="flex flex-wrap gap-2">
        <span
          v-for="label in labels"
          :key="label.title"
          class="inline-flex items-center gap-2 rounded-full bg-n-alpha-2 px-3 py-1 text-sm text-n-slate-12"
        >
          <span>{{ label.title }}</span>
          <span class="text-n-slate-11">
            {{
              $t('CONTACT_REPORTS.DETAIL.LABEL_COUNT', {
                count: label.conversationsCount,
              })
            }}
          </span>
        </span>
      </div>
      <p v-else class="mb-0 text-sm text-n-slate-11">
        {{ $t('CONTACT_REPORTS.DETAIL.NO_LABELS') }}
      </p>
    </div>

    <div class="rounded-xl border border-n-weak bg-n-solid-2 p-6">
      <div class="mb-4 flex flex-col gap-1">
        <h3 class="mb-0 text-base font-medium text-n-slate-12">
          {{ $t('CONTACT_REPORTS.DETAIL.CONTACTS_TITLE') }}
        </h3>
        <p class="mb-0 text-xs text-n-slate-11">
          {{ $t('CONTACT_REPORTS.DETAIL.CONTACTS_DESCRIPTION') }}
        </p>
      </div>
      <Table v-if="contacts.length" :table="table" />
      <p v-else class="mb-0 text-sm text-n-slate-11">
        {{ $t('CONTACT_REPORTS.DETAIL.NO_CONTACTS') }}
      </p>
    </div>

    <div class="rounded-xl border border-n-weak bg-n-solid-2 p-6">
      <div class="mb-4 flex flex-col gap-1">
        <h3 class="mb-0 text-base font-medium text-n-slate-12">
          {{ $t('CONTACT_REPORTS.OPERATION.TITLE') }}
        </h3>
        <p class="mb-0 text-xs text-n-slate-11">
          {{ $t('CONTACT_REPORTS.OPERATION.DESCRIPTION') }}
        </p>
      </div>
      <ReportSummaryMetrics :items="operationItems" />
    </div>
  </div>
</template>
